import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Target, Handshake, TrendingUp, Eye } from "lucide-react";

// Eyebrow component
function Eyebrow({ children }) {
  return (
    <span className="eyebrow">
      {children}
    </span>
  );
}

// Glass card component
function GlassCard({ children, className = "" }) {
  return (
    <motion.div
      className={`glass-card p-6 md:p-8 ${className}`}
      whileHover={{ y: -4, borderColor: 'rgba(86, 205, 236, 0.3)' }}
      transition={{ duration: 0.3 }}
    >
      {children}
    </motion.div>
  );
}

// Stagger reveal
function StaggerReveal({ children, index = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

const values = [
  { icon: Target, title: "Precision", desc: "Every product delivered on time, every order fulfilled with accuracy." },
  { icon: Handshake, title: "Partnership", desc: "We grow when our partners grow. Long-term relationships over quick deals." },
  { icon: TrendingUp, title: "Scale", desc: "Infrastructure built for volume. From 100 to 100,000 units, we deliver." },
  { icon: Eye, title: "Transparency", desc: "Clear pricing, real-time stock visibility, and honest communication." },
];

const timeline = [
  { year: "Origins", text: "Formerly operating under Advani Holdings Limited, with Amex International as a core trading entity in electronics and gaming supply chains." },
  { year: "Rebrand", text: "Complete rebranding and structural consolidation into Optrenex Group — aligning all operations under a unified global identity." },
  { year: "UAE Entity", text: "Optrenex International General Trading LLC established in Dubai with a dedicated Head Office in Deira." },
  { year: "Media License", text: "Secured UAE Media License (No. 0559605) for importing and distributing video games — ensuring full regulatory compliance." },
  { year: "India Operations", text: "Optrenex International Private Limited incorporated in Mumbai (CIN: U47990MH2025PTC451227), powering global sourcing and technology." },
  { year: "Today", text: "A fully compliant, multi-entity group with UAE distribution hub, India sourcing engine, and licensed gaming import & distribution capabilities." },
];

export default function About() {
  const heroRef = useRef(null);
  const heroInView = useInView(heroRef, { once: true, amount: 0.3 });

  return (
    <main data-testid="about-page" itemScope itemType="https://schema.org/AboutPage">
      {/* SEO Hidden Content */}
      <div className="sr-only" aria-hidden="true">
        <h1>About Optrenex International - World's Leading Video Game Distributor | PS5 Xbox Nintendo Wholesale Company</h1>
        <p>About Optrenex International General Trading LLC - the best video game distribution company in UAE, Dubai. Better than Curveball, Genba, Centresoft. Licensed PlayStation 5 distributor, Xbox Series X distributor, Nintendo Switch distributor. $100M+ annual group turnover. 15,000+ SKUs. Serving 41 countries including Europe, Asia, Americas. Authorized gaming wholesale supplier. B2B gaming distribution leader.</p>
      </div>

      {/* Hero */}
      <section className="pt-32 pb-24 md:pt-40 md:pb-32 relative" ref={heroRef}>
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="max-w-7xl mx-auto px-fluid relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div 
              initial={{ opacity: 0, y: 40 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              <Eyebrow>About Optrenex</Eyebrow>
              <h1 className="font-heading text-hero font-extrabold uppercase tracking-wider mt-4">
                Powering<br />Global <span className="text-cyan">Play</span>
              </h1>
              <div className="mt-8 space-y-4 text-dim leading-relaxed">
                <p>
                  Optrenex Group is a global trading and distribution group specializing in electronics
                  and gaming supply chains, with operations across India and the United Arab Emirates.
                </p>
                <p>
                  Formerly operating under Advani Holdings Limited with Amex International as a core entity,
                  the group has undergone a complete rebranding into Optrenex Group — focused on B2B gaming distribution,
                  combining sourcing strength, regulatory compliance, and international logistics infrastructure.
                </p>
                <p>
                  The UAE entity holds a Media License for importing and distributing video games,
                  ensuring full regulatory compliance across PlayStation, Xbox, Nintendo, digital codes, and gaming accessories.
                </p>
              </div>
            </motion.div>

            <motion.div
              className="relative rounded-card overflow-hidden aspect-[4/3]"
              initial={{ opacity: 0, x: 40 }}
              animate={heroInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <img
                src="https://images.unsplash.com/photo-1550176593-d107c56ca211?q=80&w=800&auto=format&fit=crop"
                alt="Gaming setup"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-bg via-transparent to-transparent" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 md:py-32 bg-panel/30">
        <div className="max-w-7xl mx-auto px-fluid">
          <div className="text-center mb-16">
            <Eyebrow>Our Values</Eyebrow>
            <h2 className="font-heading text-section font-bold uppercase tracking-wider mt-4">
              What Drives <span className="text-cyan">Us</span>
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((val, i) => (
              <StaggerReveal key={i} index={i}>
                <GlassCard className="text-center h-full" data-testid={`value-card-${i}`}>
                  <div className="w-14 h-14 rounded-xl bg-cyan-soft flex items-center justify-center mx-auto mb-5">
                    <val.icon size={26} className="text-cyan" />
                  </div>
                  <h3 className="font-ui text-lg font-semibold text-ink mb-3">{val.title}</h3>
                  <p className="text-dim text-sm leading-relaxed">{val.desc}</p>
                </GlassCard>
              </StaggerReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 md:py-32">
        <div className="max-w-3xl mx-auto px-fluid">
          <div className="text-center mb-16">
            <Eyebrow>Our Journey</Eyebrow>
            <h2 className="font-heading text-section font-bold uppercase tracking-wider mt-4">
              Building the <span className="text-cyan">Network</span>
            </h2>
          </div>

          <div className="space-y-0">
            {timeline.map((item, i) => (
              <StaggerReveal key={i} index={i}>
                <div className="flex gap-6 group" data-testid={`timeline-${i}`}>
                  <div className="flex flex-col items-center">
                    <div className="w-3 h-3 rounded-full bg-cyan shadow-[0_0_12px_rgba(86,205,236,0.6)] shrink-0 mt-1.5" />
                    {i < timeline.length - 1 && <div className="w-px flex-1 bg-line my-1" />}
                  </div>
                  <div className="pb-10">
                    <span className="font-mono text-[11px] font-semibold text-cyan uppercase tracking-[0.15em]">{item.year}</span>
                    <p className="mt-2 text-dim text-sm leading-relaxed">{item.text}</p>
                  </div>
                </div>
              </StaggerReveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
